/**
 * Game lifecycle status model — shared, pure helpers.
 *
 * The dashboard ("My Games") and the manager surface lean on an explicit
 * publishStatus lifecycle: draft → ready → in-progress → played, plus archived.
 * See docs/specs/2026-05-28-manager-dashboard-design.md §3.
 *
 * These helpers are intentionally free of Redux/Firebase so they can be reused
 * by selectors, components, and tests.
 */

export const PUBLISH_STATUS = {
  DRAFT: "draft",
  READY: "ready",
  IN_PROGRESS: "in-progress",
  PLAYED: "played",
  ARCHIVED: "archived",
};

// Derived UI-only pseudo-status: a draft whose clues are 100% written.
export const DISPLAY_STATUS_AWAITING = "awaiting";

const ROUND_KEY = /^round\d+$/;

/**
 * The stored lifecycle status. Missing publishStatus is treated as "draft"
 * (back-compat for games created before the lifecycle model — §3.5).
 */
export const getPublishStatus = (game) =>
  game?.publishStatus || PUBLISH_STATUS.DRAFT;

/** Round keys (round1, round2, …) present in the game's category map. */
export const getRoundKeys = (game) =>
  Object.keys(game?.categories || {})
    .filter((k) => ROUND_KEY.test(k))
    .sort((a, b) => parseInt(a.slice(5), 10) - parseInt(b.slice(5), 10));

/**
 * Clue completion for a game.
 *
 * Source of truth is the denormalized `clueStats` field maintained by the
 * questions onWrite trigger (functions/src/lastTouchedTriggers.js), which checks
 * actual clue text + answers. When absent (legacy games, before any question
 * write), we fall back to counting assigned cells in the category map — an
 * approximation that still sorts/labels sensibly. See §3.4.
 *
 * @returns {{ written: number, total: number, percentage: number }}
 */
export const computeCompletion = (game) => {
  const stats = game?.clueStats;
  if (
    stats &&
    Number.isFinite(stats.total) &&
    Number.isFinite(stats.written) &&
    stats.total > 0
  ) {
    return {
      written: stats.written,
      total: stats.total,
      percentage: Math.round((stats.written / stats.total) * 100),
    };
  }

  // Fallback: count cells (keys "1".."5") that reference a question id.
  let written = 0;
  let total = 0;
  for (const roundKey of getRoundKeys(game)) {
    // A round's categories should be an array, but a mid-generation or
    // malformed game can have a non-array here — guard so for…of can't throw.
    const cats = Array.isArray(game.categories[roundKey]) ? game.categories[roundKey] : [];
    for (const cat of cats) {
      for (let cell = 1; cell <= 5; cell += 1) {
        total += 1;
        if (cat && cat[String(cell)]) written += 1;
      }
    }
  }

  return {
    written,
    total,
    percentage: total > 0 ? Math.round((written / total) * 100) : 0,
  };
};

export const isComplete = (game) => {
  const { total, percentage } = computeCompletion(game);
  return total > 0 && percentage >= 100;
};

/**
 * In-game play progress — how many round clues have been played (`picked`).
 * Reads the denormalized `cluesPlayed` counter the selectQuestion transaction
 * maintains, against the same round-clue total as completion. Used for the
 * in-progress/played "X/Y played" copy. See spec §3.4 / §9.1.
 *
 * @returns {{ played: number, total: number, percentage: number }}
 */
export const computePlayProgress = (game) => {
  const { total } = computeCompletion(game);
  const played = Number.isFinite(game?.cluesPlayed)
    ? Math.max(0, Math.min(game.cluesPlayed, total))
    : 0;
  return {
    played,
    total,
    percentage: total > 0 ? Math.round((played / total) * 100) : 0,
  };
};

/**
 * Status used to drive the pill/UI: the stored status, except a 100%-complete
 * draft surfaces as "awaiting" (review). See §8.1.
 */
export const getDisplayStatus = (game) => {
  const status = getPublishStatus(game);
  if (status === PUBLISH_STATUS.DRAFT && isComplete(game)) {
    return DISPLAY_STATUS_AWAITING;
  }
  return status;
};

/**
 * Round with the most empty cells, for the draft "Keep editing" deep-link (§5.3).
 * Ties break to the lowest round index. All-full (or no rounds) → round1.
 */
export const neediestRound = (game) => {
  let best = null;
  for (const roundKey of getRoundKeys(game)) {
    const cats = Array.isArray(game.categories[roundKey]) ? game.categories[roundKey] : [];
    let missing = 0;
    for (const cat of cats) {
      for (let cell = 1; cell <= 5; cell += 1) {
        if (!(cat && cat[String(cell)])) missing += 1;
      }
    }
    if (best === null || missing > best.missing) {
      best = { round: roundKey, missing };
    }
  }
  if (!best || best.missing === 0) {
    return { round: best?.round || "round1", missing: 0 };
  }
  return best;
};

/**
 * Effective "touched" timestamp (ms) for sorting/hero selection.
 * max over lastTouchedAt, statusChangedAt, and createdAt/created (the legacy
 * alias) — so games missing the newer fields still sort sensibly. §3.3 / §5.1.
 * Timestamps arrive from Firestore as ISO strings.
 */
export const touchedAtMs = (game) => {
  const candidates = [
    game?.lastTouchedAt,
    game?.statusChangedAt,
    game?.createdAt,
    game?.created,
  ].filter(Boolean);
  const times = candidates
    .map((t) => new Date(t).getTime())
    .filter((n) => Number.isFinite(n));
  return times.length ? Math.max(...times) : 0;
};

/** Number of configured rounds (excludes practice/final). */
export const getRoundCount = (game) => getRoundKeys(game).length;
